import { computeReconciliation } from '@pfo/domain';
import { currentPeriod, shiftPeriod, type Period } from '@pfo/shared';
import { useId, useState } from 'react';

import { cx } from '../../../shared/lib/cx.js';
import { toAppError } from '../../../shared/lib/errors.js';
import { formatVnd } from '../../../shared/lib/money.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { MoneyInput } from '../../../shared/ui/MoneyInput.js';
import { resolveCategoryEmoji } from '../../../shared/ui/icons/categoryEmoji.js';
import { useAccounts } from '../../accounts/hooks/useAccounts.js';
import { useCategories } from '../../categories/hooks/useCategories.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import {
  confirmSummary,
  saveDraft,
  type LedgerTotal,
  type SummaryState,
} from '../data/reconciliationRepository.js';
import { useReconciliation } from '../hooks/useReconciliation.js';
import styles from './ReconciliationPage.module.css';

/**
 * "Đối chiếu ví" — once a month, the wallet's own screen is typed in and squared against the
 * ledger (plan step 1.6, spec v2 §12/§26, decision B3).
 *
 * MoMo has no export, so this is a transcription job. The one thing it must never do is post
 * the reported figure whole: the coffees typed in during the month are already on the books,
 * and what goes in is the difference. That subtraction is previewed here and recomputed
 * server-side at confirm — the books may have moved since the preview was drawn.
 */
export function ReconciliationPage() {
  const contextState = useCurrentContext();
  const accountsState = useAccounts();
  const categoriesState = useCategories();

  const accountFieldId = useId();
  const monthFieldId = useId();

  const [accountId, setAccountId] = useState('');
  // Defaults to LAST month: a wallet's monthly screen is something you read once the month
  // is over.
  const [period, setPeriod] = useState<Period | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const timeZone =
    contextState.status === 'ready' ? contextState.context.household.timezone : 'UTC';
  const shown = period ?? shiftPeriod(currentPeriod(timeZone), -1);

  // Wallets whose numbers only arrive as a monthly screen (spec §12) — reconciling an account
  // every transaction of which was typed in as it happened would be busywork.
  const summaryAccounts =
    accountsState.status === 'ready'
      ? accountsState.overview.accounts.filter(
          (account) => account.isActive && account.captureMode === 'monthly_summary',
        )
      : [];

  const chosenAccount = accountId === '' ? summaryAccounts[0]?.id : accountId;
  const reconciliation = useReconciliation(chosenAccount, shown.year, shown.month);

  if (contextState.status === 'loading' || categoriesState.status === 'loading') {
    return <LoadingState label="Đang tải màn đối chiếu…" />;
  }
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }
  if (categoriesState.status === 'error') {
    return <ErrorState message={categoriesState.message} onRetry={categoriesState.retry} />;
  }

  const { context } = contextState;
  const leafCategories = categoriesState.categories.filter(
    (category) =>
      category.categoryType === 'expense' && category.parentId !== null && category.isActive,
  );
  const nameOf = (id: string) =>
    categoriesState.categories.find((category) => category.id === id)?.name ?? 'Danh mục khác';

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Đối chiếu ví</h1>

      {summaryAccounts.length === 0 ? (
        <EmptyState
          icon="📱"
          title="Chưa có ví nào cần đối chiếu"
          message="Đặt cách ghi nhận của ví thành “Tổng hợp hằng tháng” ở màn Tài sản để đối chiếu ở đây."
        />
      ) : (
        <>
          <section className={lists.card}>
            <div className={forms.field}>
              <label htmlFor={accountFieldId}>Ví</label>
              <select
                id={accountFieldId}
                className={forms.input}
                value={chosenAccount ?? ''}
                onChange={(event) => setAccountId(event.target.value)}
              >
                {summaryAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={forms.field}>
              <label htmlFor={monthFieldId}>Tháng</label>
              <input
                id={monthFieldId}
                className={forms.input}
                type="month"
                value={`${shown.year}-${String(shown.month).padStart(2, '0')}`}
                onChange={(event) => {
                  const [year, month] = event.target.value.split('-');
                  if (year && month) setPeriod({ year: Number(year), month: Number(month) });
                }}
              />
              <p className={forms.hint}>
                Mở màn “Quản lý chi tiêu” của ví, chọn đúng tháng này, rồi nhập từng con số.
              </p>
            </div>
          </section>

          {notice ? (
            <p className={forms.success} role="status">
              {notice}
            </p>
          ) : null}
          {reconciliation.status === 'loading' ? <LoadingState label="Đang đọc sổ…" /> : null}
          {reconciliation.status === 'error' ? (
            <ErrorState message={reconciliation.message} onRetry={reconciliation.retry} />
          ) : null}

          {reconciliation.status === 'ready' && chosenAccount !== undefined ? (
            <Editor
              // Remounted per account and month: the typed-in numbers are seeded once from
              // what was read, the React way, instead of being copied in by an effect.
              key={`${chosenAccount}:${shown.year}-${shown.month}`}
              accountId={chosenAccount}
              period={shown}
              alreadyRecorded={reconciliation.data.alreadyRecorded}
              summary={reconciliation.data.summary}
              householdId={context.household.id}
              subjectId={context.selfSubject.id}
              categories={leafCategories.map((category) => ({
                id: category.id,
                name: category.name,
                icon: resolveCategoryEmoji(category),
              }))}
              nameOf={nameOf}
              onConfirmed={(text) => {
                setNotice(text);
                void reconciliation.refresh();
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

type EditorCategory = Readonly<{ id: string; name: string; icon: string }>;

/**
 * The month being reconciled: the numbers read off the wallet, what the ledger already holds
 * beside each, and what the entry will carry.
 */
function Editor({
  accountId,
  period,
  alreadyRecorded,
  summary,
  householdId,
  subjectId,
  categories,
  nameOf,
  onConfirmed,
}: {
  accountId: string;
  period: Period;
  alreadyRecorded: readonly LedgerTotal[];
  summary: SummaryState | null;
  householdId: string;
  subjectId: string;
  categories: readonly EditorCategory[];
  nameOf: (categoryId: string) => string;
  onConfirmed: (message: string) => void;
}) {
  const [reported, setReported] = useState<Record<string, number | null>>(() =>
    Object.fromEntries(
      (summary?.items ?? []).map((item) => [item.categoryId, item.reportedAmount]),
    ),
  );
  // A draft that already holds numbers — typically written by the import agent — opens as a
  // read-back to check, not as a form to retype. "Sửa số liệu" switches to the form.
  const hasDraftItems = summary !== null && summary.status === 'draft' && summary.items.length > 0;
  const [isEditing, setEditing] = useState(!hasDraftItems);
  const [isSaving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (summary?.status === 'confirmed') {
    return (
      <section className={lists.card}>
        <h2 className={lists.cardTitle}>Tháng này đã đối chiếu</h2>
        <p className={forms.hint}>
          {summary.confirmedEntryId === null
            ? 'Không cần ghi thêm giao dịch nào — sổ đã khớp với ví.'
            : 'Giao dịch điều chỉnh đã nằm trong sổ. Muốn sửa thì xoá giao dịch đó rồi đối chiếu lại.'}
        </p>
      </section>
    );
  }

  // Only the categories actually filled in are reconciled. A blank row means "I did not read a
  // number for this", not "the wallet says zero" — and zeroing it would delete real spending.
  const reportedItems = Object.entries(reported)
    .filter(([, amount]) => amount !== null && amount >= 0)
    .map(([categoryId, amount]) => ({ categoryId, reportedAmount: amount! }));

  const preview = computeReconciliation({
    reportedItems,
    ledgerExpenseByCategory: alreadyRecorded,
  });

  async function handleConfirm() {
    setSaving(true);
    setMessage(null);
    try {
      // Reviewing an existing draft confirms it as it stands — rewriting identical items
      // first would only churn row ids. The form path saves what was typed, then confirms.
      const id =
        !isEditing && summary !== null
          ? summary.id
          : await saveDraft({
              householdId,
              subjectId,
              accountId,
              year: period.year,
              month: period.month,
              items: reportedItems,
              existingId: summary?.id ?? null,
            });
      const entryId = await confirmSummary(id);
      onConfirmed(
        entryId === null
          ? 'Tháng này đã khớp sẵn — không cần ghi thêm giao dịch nào.'
          : 'Đã đối chiếu xong. Một giao dịch điều chỉnh đã được ghi vào sổ.',
      );
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {isEditing ? (
        <section className={cx(lists.card, styles.fitCard)}>
          <h2 className={lists.cardTitle}>Số ví báo, theo danh mục</h2>
          <ul className={lists.list}>
            {categories.map((category) => (
              <li key={category.id}>
                <CategoryRow
                  name={category.name}
                  icon={category.icon}
                  value={reported[category.id] ?? null}
                  alreadyRecorded={
                    alreadyRecorded.find((row) => row.categoryId === category.id)?.amount ?? 0
                  }
                  onChange={(amount) =>
                    setReported((current) => ({ ...current, [category.id]: amount }))
                  }
                />
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <section className={cx(lists.card, styles.fitCard)}>
          <h2 className={lists.cardTitle}>Bản nháp chờ xác nhận</h2>
          <p className={forms.hint}>
            {summary?.note ?? 'Số đã nhập trước đó'} · lưu{' '}
            {new Intl.DateTimeFormat('vi-VN', {
              dateStyle: 'short',
              timeStyle: 'short',
            }).format(new Date(summary?.updatedAt ?? 0))}
          </p>
          <ul className={lists.list}>
            {[...(summary?.items ?? [])]
              .sort((a, b) => b.reportedAmount - a.reportedAmount)
              .map((item) => {
                const already =
                  alreadyRecorded.find((row) => row.categoryId === item.categoryId)?.amount ?? 0;
                return (
                  <li key={item.categoryId}>
                    <div className={styles.previewRow}>
                      <span className={lists.rowText}>
                        <span className={lists.rowName}>{nameOf(item.categoryId)}</span>
                        {already !== 0 ? (
                          <span className={lists.rowMeta}>đã ghi tay {formatVnd(already)}</span>
                        ) : null}
                      </span>
                      <span className={lists.amount}>{formatVnd(item.reportedAmount)}</span>
                    </div>
                  </li>
                );
              })}
          </ul>
          <p className={styles.total}>
            <span>Ví báo tổng cộng</span>
            <strong>
              {formatVnd(
                (summary?.items ?? []).reduce((sum, item) => sum + item.reportedAmount, 0),
              )}
            </strong>
          </p>
          <button type="button" className={forms.secondary} onClick={() => setEditing(true)}>
            Sửa số liệu
          </button>
        </section>
      )}

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      {reportedItems.length > 0 ? (
        <section className={cx(lists.card, styles.fitCard)}>
          <h2 className={lists.cardTitle}>Sẽ ghi vào sổ</h2>
          <p className={forms.hint}>
            Đây là khoản <strong>chi tiêu</strong> còn thiếu so với số ví báo — xác nhận sẽ ghi thêm
            vào sổ chi cho khớp với ví, không phải tiền vào.
          </p>
          <ul className={lists.list}>
            {preview.rows
              .filter((row) => row.adjustment !== 0)
              .map((row) => (
                <li key={row.categoryId}>
                  <div className={styles.previewRow}>
                    <span className={lists.rowText}>
                      <span className={lists.rowName}>{nameOf(row.categoryId)}</span>
                      <span className={lists.rowMeta}>
                        ví báo {formatVnd(row.reportedAmount)} · đã ghi{' '}
                        {formatVnd(row.alreadyRecorded)}
                      </span>
                    </span>
                    {/* Expense to be added, so no "+": a plus sign in a money app reads as
                        income. Over-recorded rows keep the minus and the red. */}
                    <span className={cx(lists.amount, row.isOverRecorded && styles.negative)}>
                      {row.isOverRecorded ? '−' : ''}
                      {formatVnd(Math.abs(row.adjustment))}
                    </span>
                  </div>
                </li>
              ))}
          </ul>

          {preview.rows.some((row) => row.isOverRecorded) ? (
            <p className={styles.warning} role="status">
              Có danh mục đã ghi tay nhiều hơn số ví báo. Xác nhận sẽ ghi một khoản điều chỉnh ngược
              lại để sổ khớp với ví.
            </p>
          ) : null}

          {preview.unreportedCategoryIds.length > 0 ? (
            <p className={styles.warning} role="status">
              Đã ghi tay ở {preview.unreportedCategoryIds.map(nameOf).join(', ')} nhưng chưa nhập số
              cho danh mục này. Những khoản đó được giữ nguyên.
            </p>
          ) : null}

          <p className={styles.total}>
            <span>Tổng điều chỉnh</span>
            <strong>{formatVnd(preview.totalAdjustment)}</strong>
          </p>

          <button
            type="button"
            className={forms.primary}
            disabled={isSaving}
            onClick={() => void handleConfirm()}
          >
            {isSaving ? 'Đang ghi…' : 'Xác nhận đối chiếu'}
          </button>
        </section>
      ) : null}
    </>
  );
}

/** One category: what the wallet says, next to what the ledger already holds. */
function CategoryRow({
  name,
  icon,
  value,
  alreadyRecorded,
  onChange,
}: {
  name: string;
  icon: string;
  value: number | null;
  alreadyRecorded: number;
  onChange: (amount: number | null) => void;
}) {
  const inputId = useId();

  return (
    <div className={styles.categoryRow}>
      <label className={styles.categoryLabel} htmlFor={inputId}>
        <span className={styles.categoryIcon} aria-hidden="true">
          {icon}
        </span>
        <span className={lists.rowText}>
          <span className={lists.rowName}>{name}</span>
          {alreadyRecorded !== 0 ? (
            <span className={lists.rowMeta}>đã ghi tay {formatVnd(alreadyRecorded)}</span>
          ) : null}
        </span>
      </label>
      <div className={styles.categoryInput}>
        <MoneyInput id={inputId} value={value} onChange={onChange} />
      </div>
    </div>
  );
}
